import React, { useState } from 'react';
import { Alert, Button, Flex, Spin } from 'antd';
import { ArrowLeftOutlined, CheckOutlined } from '@ant-design/icons';
import {
  type AccessState,
  SUBSCRIPTION_PLANS,
  formatPrice,
  hasSubscription,
  showsTrial,
} from '@lingoping/core';
import { Echo } from '../Echo.js';
import { platform } from '../../platform.js';
import { isDevAuthMock } from '../../auth/index.js';
import { visuallyHidden } from '../../options/format.js';
import { PriceComparison } from './PriceComparison.js';
import { BillingLinks } from './BillingLinks.js';
import { BRAND } from './brand.js';
import { formatDate } from './dates.js';
import { summarizeAccess } from './accessText.js';
import { BillingError, createCheckout, waitForSubscription } from './billingApi.js';
import { openPaddleCheckout } from './paddle.js';

const GETS = [
  'Reminders that fit your day',
  'New words turned into cards for you',
  'Your cards on every device',
];

type Phase = 'idle' | 'starting' | 'open' | 'confirming' | 'done' | 'slow';

interface Props {
  access: AccessState | null;
  /** Asks the server for the access state now. */
  refreshAccess: () => Promise<AccessState | null>;
  onBack: () => void;
}

/** The Subscribe page: Echo's price comparison, the price, then Paddle's checkout. */
export const SubscribePage: React.FC<Props> = ({ access, refreshAccess, onBack }) => {
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState<string | null>(null);

  const price = SUBSCRIPTION_PLANS.monthly;
  const alreadySubscribed = hasSubscription(access);
  const thanks = phase === 'done' || phase === 'slow' || phase === 'confirming';
  const trialNote =
    showsTrial(access) && access
      ? `You keep your free days. First payment on ${formatDate(access.trial_ends_at)}.`
      : null;

  const confirmSubscription = async () => {
    setPhase('confirming');
    const result = await waitForSubscription(refreshAccess);
    setPhase(result ? 'done' : 'slow');
  };

  const startCheckout = async () => {
    setError(null);
    // The simulated development sign-in has no server: show the end of the story.
    if (isDevAuthMock()) {
      setPhase('done');
      return;
    }
    const paddle = platform.paddle;
    if (!paddle) {
      setError("Checkout isn't available here yet.");
      return;
    }
    setPhase('starting');
    try {
      const { transaction_id } = await createCheckout();
      await openPaddleCheckout(paddle, transaction_id, {
        onCompleted: () => void confirmSubscription(),
        onClosed: () => setPhase((now) => (now === 'open' ? 'idle' : now)),
      });
      setPhase((now) => (now === 'starting' ? 'open' : now));
    } catch (err) {
      if (err instanceof BillingError && err.status === 409) {
        // Already subscribed (another tab, or a checkout that finished a moment ago).
        void confirmSubscription();
        return;
      }
      console.error('Starting the checkout failed:', err);
      setPhase('idle');
      setError("The checkout couldn't be opened. Please try again.");
    }
  };

  const heading = (
    <h1 style={visuallyHidden}>
      {thanks || alreadySubscribed ? 'Thank you' : 'Subscribe to LingoPing'}
    </h1>
  );

  return (
    <Flex vertical gap={24} style={{ maxWidth: 880, margin: '0 auto' }}>
      <div>
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={onBack}>
          Back
        </Button>
      </div>
      {heading}

      {thanks || alreadySubscribed ? (
        <Flex
          vertical
          align="center"
          gap={12}
          role="status"
          style={{
            textAlign: 'center',
            padding: '32px 16px',
            background: '#fff',
            border: `2px solid ${BRAND.line}`,
            borderRadius: 16,
          }}
        >
          <Echo mood="celebrate" size={140} />
          <div style={{ fontSize: 26, fontWeight: 700, color: BRAND.deep }}>
            {thanks ? 'Thank you!' : "You're subscribed."}
          </div>
          {phase === 'confirming' && (
            <Flex align="center" gap={8}>
              <Spin size="small" />
              <span>Switching things on…</span>
            </Flex>
          )}
          {phase === 'slow' && <span>Payment received. It can take a minute to show up.</span>}
          {!thanks && access && <span>{summarizeAccess(access).text}</span>}
          <Button type="primary" size="large" onClick={onBack}>
            Back to my cards
          </Button>
        </Flex>
      ) : (
        <>
          <PriceComparison />

          <Flex
            vertical
            align="center"
            gap={14}
            style={{
              maxWidth: 440,
              width: '100%',
              margin: '0 auto',
              padding: '28px 24px',
              background: '#fff',
              border: `2px solid ${BRAND.main}`,
              borderRadius: 18,
              boxShadow: '0 10px 30px rgba(91, 80, 232, 0.18)',
              boxSizing: 'border-box',
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <span style={{ fontSize: 40, fontWeight: 800, color: BRAND.deep }}>
                {formatPrice(price.amountCents, price.currency)}
              </span>
              <span style={{ fontSize: 18, color: '#475569' }}> / month</span>
              <div style={{ fontSize: 13, color: '#5b6472' }}>VAT included</div>
            </div>

            <ul style={{ listStyle: 'none', margin: 0, padding: 0, alignSelf: 'stretch' }}>
              {GETS.map((line) => (
                <li key={line} style={{ display: 'flex', gap: 10, padding: '3px 0' }}>
                  <CheckOutlined style={{ color: '#16a34a', marginTop: 4 }} aria-hidden />
                  {line}
                </li>
              ))}
            </ul>

            {trialNote && (
              <div style={{ textAlign: 'center', fontWeight: 600, color: BRAND.dark }}>
                {trialNote}
              </div>
            )}

            {error && (
              <Alert type="error" showIcon title={error} style={{ alignSelf: 'stretch' }} />
            )}

            <Button
              type="primary"
              size="large"
              block
              loading={phase === 'starting'}
              onClick={() => void startCheckout()}
              style={{ height: 48, fontSize: 17, background: BRAND.main }}
            >
              Subscribe
            </Button>

            <BillingLinks />
          </Flex>
        </>
      )}
    </Flex>
  );
};
